import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Container
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import GetAppIcon from '@material-ui/icons/GetApp';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import {useTranslation} from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  statusBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  statusBoxSuccess: {
    backgroundColor: theme.palette.success.light + '20',
    border: `2px solid ${theme.palette.success.main}`,
  },
  statusBoxError: {
    backgroundColor: theme.palette.error.light + '20',
    border: `2px solid ${theme.palette.error.main}`,
  },
  statusIcon: {
    fontSize: 48,
  },
  browserItem: {
    marginBottom: theme.spacing(1),
  },
  downloadButton: {
    marginLeft: 'auto',
  },
  infoBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light + '15',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(3),
  },
  warningBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.warning.light + '15',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  currentBrowserInfo: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
  },
}));

export default function BrowserCompatibility() {
  const classes = useStyles();
  const {t} = useTranslation();
  const isWebUSBSupported = navigator.usb !== undefined;
  const browserInfo = getBrowserInfo();

  const supportedBrowsers = [
    {
      name: 'Google Chrome',
      version: t('browserCheck.chrome.version'),
      supported: true,
      recommended: true,
      downloadUrl: 'https://www.google.com/chrome/',
    },
    {
      name: 'Microsoft Edge',
      version: t('browserCheck.edge.version'),
      supported: true,
      recommended: false,
      downloadUrl: 'https://www.microsoft.com/edge',
    },
    {
      name: 'Opera',
      version: t('browserCheck.opera.version'),
      supported: true,
      recommended: false,
      downloadUrl: 'https://www.opera.com/',
    },
    {
      name: 'Firefox',
      version: '',
      supported: false,
      recommended: false,
      reason: t('browserCheck.firefox.reason'),
    },
    {
      name: 'Safari',
      version: '',
      supported: false,
      recommended: false,
      reason: t('browserCheck.safari.reason'),
    },
  ];

  return (
    <Container maxWidth="md" className={classes.container}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h4" gutterBottom>
            {t('browserCheck.title')}
          </Typography>

          {/* 当前状态 */}
          <Box
            className={`${classes.statusBox} ${
              isWebUSBSupported ? classes.statusBoxSuccess : classes.statusBoxError
            }`}
          >
            {isWebUSBSupported ? (
              <CheckCircleIcon className={classes.statusIcon} style={{color: '#4caf50'}} />
            ) : (
              <CancelIcon className={classes.statusIcon} style={{color: '#f44336'}} />
            )}
            <Box>
              <Typography variant="h6">
                {isWebUSBSupported
                  ? t('browserCheck.status.supported')
                  : t('browserCheck.status.notSupported')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isWebUSBSupported
                  ? t('browserCheck.status.supportedDesc')
                  : t('browserCheck.status.notSupportedDesc')}
              </Typography>
            </Box>
          </Box>

          {/* 当前浏览器信息 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('browserCheck.currentBrowser')}
          </Typography>
          <Box className={classes.currentBrowserInfo}>
            <Typography variant="body1">
              <strong>{t('browserCheck.browserName')}:</strong> {browserInfo.name}
            </Typography>
            <Typography variant="body1">
              <strong>{t('browserCheck.browserVersion')}:</strong> {browserInfo.version}
            </Typography>
            <Typography variant="body1">
              <strong>WebUSB {t('browserCheck.support')}:</strong>{' '}
              {isWebUSBSupported ? (
                <Chip
                  label={t('browserCheck.supported')}
                  color="primary"
                  size="small"
                  icon={<CheckCircleIcon />}
                />
              ) : (
                <Chip
                  label={t('browserCheck.notSupported')}
                  color="secondary"
                  size="small"
                  icon={<CancelIcon />}
                />
              )}
            </Typography>
          </Box>

          <Divider />

          {/* 支持的浏览器列表 */}
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('browserCheck.supportedBrowsers')}
          </Typography>
          <List>
            {supportedBrowsers.map((browser, index) => (
              <React.Fragment key={browser.name}>
                <ListItem className={classes.browserItem}>
                  <ListItemIcon>
                    {browser.supported ? (
                      <CheckCircleIcon style={{color: '#4caf50'}} />
                    ) : (
                      <CancelIcon style={{color: '#f44336'}} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{browser.name}</span>
                        {browser.recommended && (
                          <Chip label={t('browserCheck.recommended')} color="primary" size="small" />
                        )}
                      </Box>
                    }
                    secondary={browser.supported ? browser.version : browser.reason}
                  />
                  {browser.supported && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GetAppIcon />}
                      className={classes.downloadButton}
                      onClick={() => window.open(browser.downloadUrl, '_blank')}
                    >
                      {t('browserCheck.download')}
                    </Button>
                  )}
                </ListItem>
                {index < supportedBrowsers.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>

          {/* 为什么不支持说明 */}
          <Box className={classes.infoBox}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <InfoIcon color="primary" />
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>{t('browserCheck.whyNotSupported.title')}</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{margin: 0, paddingLeft: 20}}>
                    <li>{t('browserCheck.whyNotSupported.security')}</li>
                    <li>{t('browserCheck.whyNotSupported.privacy')}</li>
                    <li>{t('browserCheck.whyNotSupported.architecture')}</li>
                  </ul>
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* WebUSB 要求 */}
          <Box className={classes.warningBox}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <WarningIcon style={{color: '#ff9800'}} />
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>{t('browserCheck.requirements.title')}</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{margin: 0, paddingLeft: 20}}>
                    <li>{t('browserCheck.requirements.https')}</li>
                    <li>{t('browserCheck.requirements.userGesture')}</li>
                    <li>{t('browserCheck.requirements.devicePermission')}</li>
                  </ul>
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

function getBrowserInfo() {
  const ua = navigator.userAgent;
  let name = 'Unknown';
  let version = 'Unknown';

  if (ua.includes('Firefox/')) {
    name = 'Firefox';
    version = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Edg/')) {
    name = 'Microsoft Edge';
    version = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Chrome/') && !ua.includes('Edg/')) {
    name = 'Google Chrome';
    version = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    name = 'Safari';
    version = ua.match(/Version\/(\d+\.\d+)/)?.[1] || 'Unknown';
  } else if (ua.includes('OPR/') || ua.includes('Opera/')) {
    name = 'Opera';
    version = ua.match(/(?:OPR|Opera)\/(\d+\.\d+)/)?.[1] || 'Unknown';
  }

  return {name, version};
}
